import { ColumnDef } from '@tanstack/react-table';
import { CustomTable } from 'components/Table/CustomTable';
import { ActionCell } from 'components/Table/Cell/ActionCell';
import { DateTimeCell } from 'components/Table/Cell/DateTimeCell';
import { useGetCustomReactTable } from 'components/Table/useGetTableSet';
import { TableQueryState } from 'components/Table/useTableQueryState';

import { useMemo, useState } from 'react';
import { DeleteEntityById } from 'components/Form/DeleteEntityById';
import { AnimatedIcon } from 'components/Icon/AnimatedIcon';
import { CaseImporterUpdate } from './CaseImporterUpdate';
import {
  OrganizationCaseImporterResponse,
  OrganizationCaseImporterResponsePagedData,
} from 'api/core';
import {
  useDeleteCaseImporter,
  useGetCaseImporter,
  useInvokeCaseImporter,
  useResetCaseHashesForCaseImporter,
} from 'api/useCaseImportersApi';

interface TableProps {
  data?: OrganizationCaseImporterResponsePagedData;
  isFetching: boolean;
  queryState: TableQueryState;
}

export const CaseImportersTable = ({
  data,
  isFetching,
  queryState,
}: TableProps) => {
  const [editTarget, setEditTarget] =
    useState<OrganizationCaseImporterResponse | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<OrganizationCaseImporterResponse | null>(null);

  const { mutateAsync: invokeCaseImporterAsync, isPending: isPendingInvoke } =
    useInvokeCaseImporter();
  const { mutateAsync: resetCaseHashesAsync, isPending: isPendingReset } =
    useResetCaseHashesForCaseImporter();

  const columns = useMemo(() => {
    const baseColumns: ColumnDef<OrganizationCaseImporterResponse>[] = [
      {
        accessorKey: 'caseImporter.name',
        header: 'Integration',
      },
      {
        accessorKey: 'lastTriggeredUtc',
        header: 'Sidst udført',
        cell: (e) => <DateTimeCell value={e.row.original.lastTriggeredUtc} />,
      },
      {
        accessorKey: 'lastModifiedUtc',
        header: 'Sidst redigeret',
        cell: (e) => <DateTimeCell value={e.row.original.lastModifiedUtc} />,
      },
      {
        accessorKey: 'createdUtc',
        header: 'Oprettet',
        cell: (e) => <DateTimeCell value={e.row.original.createdUtc} />,
      },
      {
        accessorKey: '_',
        header: 'Handlinger',
        enableSorting: false,
        cell: (e) => (
          <div className="flex space-x-5 md:space-x-3">
            <ActionCell
              icon={
                <AnimatedIcon
                  icon="refresh-icon"
                  className="h-8 md:h-6 w-8 md:w-6"
                />
              }
              buttonDisabled={isPendingReset}
              tooltip="Marker alle sager fra denne integration som usynkroniseret (data hentes ind på ny ved næste kørsel)"
              onClick={() => resetCaseHashesAsync({ id: e.row.original.id })}
            />
            <ActionCell
              icon={
                <AnimatedIcon
                  icon="play-icon"
                  className="h-8 md:h-6 w-8 md:w-6"
                />
              }
              buttonDisabled={
                isPendingInvoke ||
                new Date().getTime() -
                  new Date(e.row.original.lastTriggeredUtc).getTime() <
                  10000
              }
              tooltip="Kør integrationen nu"
              onClick={() => invokeCaseImporterAsync({ id: e.row.original.id })}
            />
            <ActionCell
              icon={
                <AnimatedIcon
                  icon="pencil-icon"
                  className="h-8 md:h-6 w-8 md:w-6"
                />
              }
              onClick={() => setEditTarget(e.row.original)}
            />
            <ActionCell
              icon={
                <AnimatedIcon
                  icon="trash-icon"
                  className="h-8 md:h-6 w-8 md:w-6"
                />
              }
              onClick={() => setDeleteTarget(e.row.original)}
            />
          </div>
        ),
      },
    ];
    return baseColumns;
  }, [
    isPendingReset,
    isPendingInvoke,
    resetCaseHashesAsync,
    invokeCaseImporterAsync,
  ]);

  const table = useGetCustomReactTable(
    data?.data ?? [],
    data?.page.totalPages ?? -1,
    columns,
    queryState
  );

  return (
    <>
      <CustomTable table={table} isLoading={isFetching} />
      {editTarget ? (
        <CaseImporterUpdate
          id={editTarget?.id}
          isInitialOpen={true}
          onClosed={() => setEditTarget(null)}
        />
      ) : null}
      {deleteTarget ? (
        <DeleteEntityById
          id={deleteTarget?.id}
          isInitialOpen={true}
          onClosed={() => setDeleteTarget(null)}
          title={deleteTarget?.caseImporter?.name ?? 'Integration'}
          getter={useGetCaseImporter}
          deleter={useDeleteCaseImporter}
        />
      ) : null}
    </>
  );
};
